import { Alert } from "@msuite/ui/alert";
import { useTranslation } from "@msuite/ui/localization";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { Calculation } from "@msuite/utils/types";

/** Props Interface */
interface CalculationLockedAlertProps {
	calculation: Pick<Calculation, "state" | "lockedAt" | "lockSource" | "_lockedBy">;
}

/**
 * Says that a calculation is locked, by whom and since when — and whether a person locked it or
 * the approval of a settlement record did. Renders nothing for an unlocked calculation.
 */
export function CalculationLockedAlert({ calculation }: CalculationLockedAlertProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const params = {
		name: guardString(calculation._lockedBy?.name),
		date: calculation.lockedAt ? moment(calculation.lockedAt).longFormat() : guardString(null),
	};

	/** Guard */
	if (calculation.state !== "locked") return null;

	/** Render */
	return (
		<Alert
			variant="info"
			title={t("calculations.locked")}
			description={
				calculation.lockSource === "approval"
					? t("calculations.locked_by_approval_banner", params)
					: t("calculations.locked_banner", params)
			}
		/>
	);
}
